import React, { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import posthog from "posthog-js";
import { toast } from "sonner";

interface JobDescriptionWizardProps {
  workspaceId: string;
  onProcessed: (extractedData: any) => void;
  onBack: () => void;
  onClose: () => void;
}

const JobDescriptionWizard: React.FC<JobDescriptionWizardProps> = ({
  workspaceId,
  onProcessed,
  onBack,
  onClose,
}) => {
  const [inputText, setInputText] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [userMessage, setUserMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [userMessage, processing]);

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setUploadedFileName(file.name);
    setInputText(""); // Clear text input when file is uploaded
  };

  // Handle text input submission
  const handleSendMessage = () => {
    if (inputText.trim()) {
      setUserMessage(inputText);
      processInput(inputText);
      setInputText("");
    } else if (uploadedFile) {
      setUserMessage(`Uploaded file: ${uploadedFileName}`);
      processFileUpload();
    }
  };

  // Process text input
  const processInput = async (text: string) => {
    setProcessing(true);

    try {
      const response = await fetch(
        `/api/v1/jobs/ai/description?workspaceId=${workspaceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ description: text }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to process job description");
      }
      const extractedData = await response.json();

      posthog.capture("job_description_ai_generated", extractedData);

      onProcessed(extractedData);
      toast.success("Job details extracted successfully!");
    } catch (error) {
      console.error("Error processing job description:", error);
      toast.error("Failed to extract job details from description");
    } finally {
      setProcessing(false);
    }
  };

  // Process file upload
  const processFileUpload = async () => {
    if (!uploadedFile) return;

    setProcessing(true);

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("workspaceId", workspaceId);

      const response = await fetch(
        `/api/v1/jobs/process-job-description?workspaceId=${workspaceId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to process uploaded file");
      }

      const extractedData = await response.json();
      onProcessed(extractedData);
      toast.success("Job details extracted successfully!");
    } catch (error) {
      console.error("Error processing uploaded file:", error);
      toast.error("Failed to extract job details from file");
    } finally {
      setProcessing(false);
    }
  };

  // Handle keyboard submission (Enter key)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="space-y-6">
      {/* Input area */}
      <div className="border-neutral-200 bg-white">
        <div className="relative flex items-center">
          <textarea
            className="flex-1 rounded-md border-neutral-300 pr-24 py-2 resize-none focus:border-neutral-500 focus:ring-neutral-500 text-sm mt-1"
            placeholder={
              uploadedFile
                ? "File selected. Click send to process."
                : "Type or paste your job description here to autofill the form..."
            }
            rows={2}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={processing || !!uploadedFile}
          />

          {/* File upload button */}
          <div className="absolute right-12 top-1/2 -translate-y-1/2">
            <label
              htmlFor="file-upload"
              className={`cursor-pointer ${uploadedFile ? "text-green-500" : "text-neutral-500 hover:text-neutral-700"} ${processing ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span className="sr-only">Upload file</span>
            </label>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileUpload}
              disabled={processing}
            />
          </div>

          {/* Send button */}
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 disabled:opacity-50"
            onClick={handleSendMessage}
            disabled={processing || (!inputText.trim() && !uploadedFile)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 2L11 13"></path>
              <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
            </svg>
          </button>
        </div>

        {/* Show uploaded file name */}
        {uploadedFileName && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-neutral-500">
              📎 {uploadedFileName}
            </span>
            <button
              className="text-xs text-red-500 hover:text-red-700"
              onClick={() => {
                setUploadedFile(null);
                setUploadedFileName("");
              }}
              disabled={processing}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(JobDescriptionWizard);
